import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useBuilding } from "../../context/BuildingContext";
import { bankTransactionsApi } from "../../services/api";
import PageHeader from "../../components/common/PageHeader";
import ErrorAlert from "../../components/common/ErrorAlert";
import { BankTransactionType } from "@akfdev-com/shared/src/models";

interface TransactionForm {
  description: string;
  date: string;
  type: BankTransactionType;
  grossValue: string;
  reference: string;
  reconciled: boolean;
}

const transactionTypeOptions = Object.values(BankTransactionType).map((v) => ({
  value: v,
  label: v.replace(/([A-Z])/g, " $1").trim(),
}));

const today = new Date().toISOString().split("T")[0];

const defaultForm: TransactionForm = {
  description: "",
  date: today,
  type: BankTransactionType.Payment,
  grossValue: "0",
  reference: "",
  reconciled: false,
};

const BankTransactionNew: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentBuilding } = useBuilding();

  const buildingId =
    searchParams.get("buildingId") || currentBuilding?.id || "";
  const bankAccountId = searchParams.get("bankAccountId") ?? "";
  const bankAccountName = searchParams.get("bankAccountName") ?? "";

  const [form, setForm] = useState<TransactionForm>(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;
    if (type === "checkbox") {
      setForm((prev) => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked,
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!buildingId || !bankAccountId) {
      setError("Missing building or account context.");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await bankTransactionsApi.create({
        ...form,
        grossValue: Math.round(parseFloat(form.grossValue || "0") * 100),
        bankAccountId,
        bankAccountName,
        buildingId,
      });

      toast.success("Transaction added successfully.");
      navigate(`/dashboard/banking/${bankAccountId}?buildingId=${buildingId}`);
    } catch (err) {
      console.error("Error creating transaction:", err);
      setError("Failed to create transaction. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Add Transaction"
        description={
          bankAccountName
            ? `Account: ${bankAccountName}`
            : "Manual transaction entry"
        }
      />

      {error && <ErrorAlert message={error} />}

      <form
        onSubmit={handleSubmit}
        className="mt-6 bg-white shadow rounded-lg p-4 sm:p-6 space-y-5 w-full max-w-2xl"
      >
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label
              className="block text-sm font-medium text-gray-700"
              htmlFor="description"
            >
              Description <span className="text-red-500">*</span>
            </label>
            <input
              id="description"
              name="description"
              type="text"
              required
              value={form.description}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 text-base sm:text-sm"
            />
          </div>

          <div>
            <label
              className="block text-sm font-medium text-gray-700"
              htmlFor="date"
            >
              Date <span className="text-red-500">*</span>
            </label>
            <input
              id="date"
              name="date"
              type="date"
              required
              value={form.date}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 text-base sm:text-sm"
            />
          </div>

          <div>
            <label
              className="block text-sm font-medium text-gray-700"
              htmlFor="type"
            >
              Type
            </label>
            <select
              id="type"
              name="type"
              value={form.type}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 text-base sm:text-sm"
            >
              {transactionTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              className="block text-sm font-medium text-gray-700"
              htmlFor="grossValue"
            >
              Gross Value (£)
            </label>
            <p className="text-xs text-gray-400">
              Positive = money in, negative = money out
            </p>
            <input
              id="grossValue"
              name="grossValue"
              type="number"
              step="0.01"
              value={form.grossValue}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 text-base sm:text-sm"
            />
          </div>

          <div>
            <label
              className="block text-sm font-medium text-gray-700"
              htmlFor="reference"
            >
              Reference
            </label>
            <input
              id="reference"
              name="reference"
              type="text"
              value={form.reference}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 text-base sm:text-sm"
            />
          </div>

          <div className="sm:col-span-2 flex items-center gap-2">
            <input
              id="reconciled"
              name="reconciled"
              type="checkbox"
              checked={form.reconciled}
              onChange={handleChange}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="reconciled" className="text-sm text-gray-700">
              Reconciled
            </label>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end pt-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-base sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 min-h-[44px] sm:min-h-0"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-base sm:text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 disabled:opacity-50 min-h-[44px] sm:min-h-0"
          >
            {submitting ? "Adding…" : "Add Transaction"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BankTransactionNew;
